import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  FormHelperText,
  Box,
} from '@mui/material';
import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import { ApiRequest_UpdatePhone_User, useUpdatePhone } from '@/api/endpoints/user/useUpdatePhone';
import { PhoneIcon } from '@/assets/icons/PhoneIcon';
import { useAuth } from '@/context/AuthProvider';

import FormTextField from '@/components/form/FormTextField';

interface PhoneNumberModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  title?: string;
  description?: string;
}

const PhoneNumberModal: React.FC<PhoneNumberModalProps> = ({
  open,
  onClose,
  onSuccess,
  title = 'Phone Number Required',
  description = 'We need your phone number to contact vendors on your behalf. This helps us provide better service and faster responses.',
}) => {
  const { user } = useAuth();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ApiRequest_UpdatePhone_User>({
    defaultValues: {
      phone: user?.phone ?? '',
    },
  });

  const [formError, setFormError] = useState<Error | null>(null);

  const updatePhoneMutation = useUpdatePhone({
    onError: error => {
      setFormError(error);
    },
    onSuccess: () => {
      // Call onSuccess first, then close after a brief delay to allow next dialog to open
      onSuccess?.();
      setTimeout(() => {
        onClose();
      }, 100);
    },
  });

  // Function to format phone number to standard format
  const formatPhoneNumber = (phone: string): string => {
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Handle different cases
    if (digitsOnly.length === 10) {
      // US number without country code: 1234567890 -> 1234567890
      return digitsOnly;
    } if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      // US number with country code: 11234567890 -> 1234567890
      return digitsOnly.slice(1);
    } if (digitsOnly.length >= 10) {
      // International or other format, take last 10 digits
      return digitsOnly.slice(-10);
    }
    
    // If less than 10 digits, return as is (will be caught by validation)
    return digitsOnly;
  };

  const onSubmit = useCallback(
    (data: ApiRequest_UpdatePhone_User) => {
      setFormError(null);
      
      // Format the phone number before sending
      const formattedData = {
        ...data,
        phone: formatPhoneNumber(data.phone),
      };
      
      return updatePhoneMutation.mutateAsync(formattedData);
    },
    [updatePhoneMutation]
  );

  const handleCancel = () => {
    setFormError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: '1.25rem',
          color: 'secondary.900',
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <PhoneIcon width={24} height={24} />
        {title}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography variant="body1" color="text.secondary">
            {description}
          </Typography>

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.primary"
                  sx={{ mb: 1, fontWeight: 500 }}
                >
                  Phone Number *
                </Typography>
                <FormTextField
                  placeholder="Enter your phone number in any format"
                  fullWidth
                  error={!!errors.phone}
                  helperText={errors.phone?.message?.toString()}
                  control={control}
                  required
                  {...register('phone', {
                    required: 'Phone number is required',
                    validate: (value) => {
                      const digitsOnly = value.replace(/\D/g, '');
                      if (digitsOnly.length < 10) {
                        return 'Phone number must have at least 10 digits';
                      }
                      return true;
                    },
                  })}
                />
              </Box>

              {!!formError && <FormHelperText error>{formError.message}</FormHelperText>}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button variant="outlined" onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <LoadingButton
          loading={isSubmitting}
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }}
        >
          Save Phone Number
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default PhoneNumberModal;